import React, { useEffect, useState } from "react"
import { useSnapshot } from "valtio"
import { Step } from "../../types/wizard"
import { Checkbox } from "../commons/Checkbox"
import { saveEmblemData, wizardStore } from "../../stores/wizard"
import './QuickView.css'
import RadioGroup from "./commonComps/RadioGroup"

type ScaleDimensions = "scaleWidth" | "scaleHeight"

export const Size: React.FC<{ step: Step }> = ({ step }) => {
  const wizSnap = useSnapshot(wizardStore)
  const [width, setWidth] = useState("")
  const [height, setHeight] = useState("")
  const [widthError, setWidthError] = useState("")
  const [heightError, setHeightError] = useState("")
  const [scaleWidth, setScaleWidth] = useState(false)
  const [scaleHeight, setScaleHeight] = useState(false)
  const [selectedSizeOption, setSelectedSizeOption] = useState(step?.selectedValue ?? "custom");
  const inputWidth = Boolean(wizSnap?.requireIndication) && 'max-w-[140px]'; 

  useEffect(() => {
    const sizeData = wizSnap.emblemData?.[step.id]
    if (sizeData) {
      if (sizeData.width) setWidth(sizeData.width)
      if (sizeData.height) setHeight(sizeData.height)
      if (sizeData.scaleWidth) setScaleWidth(sizeData.scaleWidth)
      if (sizeData.scaleHeight) setScaleHeight(sizeData.scaleHeight)
    }
  }, [step.id, wizSnap.emblemData])

  const handleScale = (e: React.ChangeEvent<HTMLInputElement>, dimension: ScaleDimensions) => {
    const isChecked = e.target.checked
    dimension === "scaleWidth" ? setScaleWidth(isChecked) : setScaleHeight(isChecked)
    saveEmblemData(step.id, { [dimension]: isChecked })
  };

  const validateWidth = (value: number) => {
    let errorMessage = ""
    const { widthMax, widthMin } = step.dimensionsConstraints;
    if ((value || value === 0) && (widthMax || widthMin)) {
      if (value > widthMax) errorMessage = `The maximum width is ${widthMax}"`
      else if (value < widthMin) errorMessage = `The minimum height is ${widthMin}"`
    }
    setWidthError(errorMessage)
    return !errorMessage
  }

  const validateHeight = (value: number) => {
    let errorMessage = ""
    const { heightMax, heightMin } = step.dimensionsConstraints;
    if ((value || value === 0) && (heightMax || heightMin)) {
      if (value > heightMax) errorMessage = `The maximum height is ${heightMax}"`
      else if (value < heightMin) errorMessage = `The minimum height is ${heightMin}"`
    }
    setHeightError(errorMessage)
    return !errorMessage
  }

  const handleWidth = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    validateWidth(Number(value))
    setWidth(value)
    saveEmblemData(step.id, { width: value })
  }

  const handleHeight = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    validateHeight(Number(value))
    setHeight(value)
    saveEmblemData(step.id, { height: value })
  }

  const handleSizeChange = (value: string) => {
    setSelectedSizeOption(value);
  };

  return (
    <div className="w-full mt-5">
      <div className="w-full pb-4">
        <RadioGroup step={step} onChange={handleSizeChange} />
      </div>
      {selectedSizeOption?.toLowerCase() === 'custom' &&
        <>
          {!scaleWidth ? (
            <div className="flex flex-col mb-4">
              <label className="uppercase font-bold mb-2.5">Width</label>
              <div className="flex items-center gap-x-4 flex-wrap">
                <div className="relative w-full sm:w-auto">
                  <input
                    type="number"
                    className={`p-2.5 pr-10 ${inputWidth ?? "w-full"} rounded outline-none`}
                    style={{ border: "1px solid #ccc" }}
                    onChange={handleWidth}
                    name="width"
                    value={width}
                  />
                  <span className="absolute right-3 top-1/2 transform -translate-y-1/2 font-bold text-gray-500">
                    in.
                  </span>
                </div>
                <Checkbox
                  checked={scaleHeight}
                  name="scaleHeight"
                  label="Scale Height"
                  onChange={(e) => handleScale(e, "scaleHeight")}
                />
                <span className="relative text-error text-xs absolute top-2">{widthError}</span>
              </div>

            </div>
          ) : null}

          {!scaleHeight ? (
            <div className="flex flex-col mb-4">
              <label className="uppercase font-bold mb-2.5">Height</label>
              <div className="flex items-center gap-x-4 flex-wrap">
                <div className="relative w-full sm:w-auto">
                  <input
                    type="number"
                    className={`p-2.5 pr-10 ${inputWidth ?? "w-full"} rounded outline-none`}
                    style={{ border: "1px solid #ccc" }}
                    onChange={handleHeight}
                    name="height"
                    value={height}
                  />
                  <span className="absolute right-3 top-1/2 transform -translate-y-1/2 font-bold text-gray-500">
                    in.
                  </span>
                </div>
                <Checkbox
                  checked={scaleWidth}
                  name="scaleWidth"
                  label="Scale Width"
                  onChange={(e) => handleScale(e, "scaleWidth")}
                />
                <span className="relative text-error text-xs absolute top-2">{heightError}</span>
              </div>
            </div>
          ) : null}
        </>
      }
    </div>
  )
}